import { useIntl } from "react-intl";
import { Box, styled, useTheme, Chip, darken, Stack } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { ActivityStatus, JourneyActivityType, UserJourneyActivityResponse } from "user-journey/consts/journeyActivity";

import { formatDistance } from "localization/utils";

import { ReactComponent as CalendarIconSVG } from "assets/icons/dataIcon/calendar-filled.svg";
import { ReactComponent as TimeIconSVG } from "assets/icons/dataIcon/time-outlined.svg";
import { ReactComponent as QuestionIconSVG } from "assets/icons/dataIcon/questionWaves-filled.svg";

import { getInvertColor } from "utils/themeUtils";

interface ActivityCardProps {
	status: ActivityStatus;
}

export const buttonColor = {
	[ActivityStatus.IN_PROGRESS]: "primary",
	[ActivityStatus.READY]: "primary",
	[ActivityStatus.COMPLETED]: "success",
	[ActivityStatus.FAILED]: "error",
	[ActivityStatus.OVERDUE]: "error",
	[ActivityStatus.REJECTED]: "error",
	[ActivityStatus.SOON]: "warning",
};

export const ActivityCard = styled("div", {
	shouldForwardProp: (prop) => !["status"].includes(prop as string),
})<ActivityCardProps>(({ theme, status }) => {
	const bgcolor = {
		[ActivityStatus.LOCKED]: theme.palette.dirtyWhite["200"],
		[ActivityStatus.IN_PROGRESS]: theme.palette.primary["50"],
		[ActivityStatus.READY]: theme.palette.primary["50"],
		[ActivityStatus.COMPLETED]: theme.palette.success["50"],
		[ActivityStatus.FAILED]: theme.palette.error["50"],
		[ActivityStatus.OVERDUE]: theme.palette.error["50"],
		[ActivityStatus.REJECTED]: theme.palette.error["50"],
		[ActivityStatus.SOON]: theme.palette.warning["50"],
		[ActivityStatus.WAITING_FOR_APPROVAL]: theme.palette.dirtyWhite["200"],
	};

	const borderColor = {
		[ActivityStatus.LOCKED]: theme.palette.dirtyWhite["400"],
		[ActivityStatus.IN_PROGRESS]: theme.palette.primary.main,
		[ActivityStatus.READY]: theme.palette.primary.main,
		[ActivityStatus.COMPLETED]: theme.palette.success.main,
		[ActivityStatus.FAILED]: theme.palette.error.main,
		[ActivityStatus.OVERDUE]: theme.palette.error.main,
		[ActivityStatus.REJECTED]: theme.palette.error.main,
		[ActivityStatus.SOON]: theme.palette.warning.main,
		[ActivityStatus.WAITING_FOR_APPROVAL]: theme.palette.dirtyWhite["400"],
	};

	return {
		position: "relative",
		display: "flex",
		flexDirection: "column",

		padding: theme.spacing(4),
		paddingTop: theme.spacing(6),
		minWidth: 320,
		maxWidth: 320,
		height: 340,

		backgroundColor: bgcolor[status],
		border: "1px solid",
		borderColor: borderColor[status],
		borderRadius: theme.spacing(2),

		[theme.breakpoints.down("xxl")]: {
			minWidth: 304,
			maxWidth: 304,
			height: 336,
		},
		[theme.breakpoints.down("xl")]: {
			minWidth: 288,
			maxWidth: 288,
			height: 324,
		},
		[theme.breakpoints.down("lg")]: {
			minWidth: 256,
			maxWidth: 256,
			height: 324,
		},
		[theme.breakpoints.down("sm")]: {
			minWidth: 240,
			maxWidth: 240,
			height: 304,
		},
	};
});

export const ActivityStatusChip = ({ status }: { status: ActivityStatus }) => {
	const intl = useIntl();

	const theme = useTheme();

	const statusText = `USER-JOURNEY.activity.card.status.${status}`;

	const chipColor = {
		[ActivityStatus.LOCKED]: theme.palette.dirtyWhite["1000"],
		[ActivityStatus.IN_PROGRESS]: theme.palette.primary.main,
		[ActivityStatus.READY]: theme.palette.primary.main,
		[ActivityStatus.COMPLETED]: theme.palette.success.main,
		[ActivityStatus.FAILED]: theme.palette.error.main,
		[ActivityStatus.OVERDUE]: theme.palette.error.main,
		[ActivityStatus.REJECTED]: theme.palette.error.main,
		[ActivityStatus.SOON]: theme.palette.warning.main,
		[ActivityStatus.WAITING_FOR_APPROVAL]: theme.palette.dirtyWhite["1000"],
	};

	return (
		<Chip
			label={intl.formatMessage({ id: statusText })}
			size="xs"
			sx={(theme) => ({
				position: "absolute",
				top: 0,
				right: theme.spacing(4),
				transform: "translateY(-50%)",
				color: [ActivityStatus.IN_PROGRESS, ActivityStatus.READY].includes(status)
					? getInvertColor(theme.palette.primary.main)
					: "#FFF",
				backgroundColor: chipColor[status],
				borderRadius: theme.spacing(2),
			})}
		/>
	);
};

export const ActivityHeader = styled("div")(({ theme }) => {
	const isLight = getInvertColor(theme.palette.primary.main) === theme.palette.dirtyWhite["2000"];

	return {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		gap: theme.spacing(3),

		"& .MuiTypography-root": {
			display: "-webkit-box",
			WebkitLineClamp: 2,
			WebkitBoxOrient: "vertical",
			overflow: "hidden",
			textOverflow: "ellipsis",
			height: theme.spacing(12),
			textAlign: "center",
			fontWeight: 700,
		},

		"& svg": {
			width: theme.spacing(10),
			height: theme.spacing(10),

			"& .organizationColor__fill": {
				fill: isLight ? darken(theme.palette.primary.main, 0.5) : theme.palette.primary.main,
			},

			"& .organizationColor__stroke": {
				stroke: isLight ? darken(theme.palette.primary.main, 0.5) : theme.palette.primary.main,
			},
		},
	};
});

export const MbtiResult = styled(Box)<Pick<ActivityCardProps, "status">>(({ status, theme }) => {
	return {
		borderRadius: theme.spacing(2),
		border: "1px dashed",
		borderColor: status === ActivityStatus.COMPLETED ? theme.palette.success.main : theme.palette.dirtyWhite["400"],
		width: "100%",
		marginTop: theme.spacing(1),

		"& .MuiTypography-root": {
			color: status === ActivityStatus.COMPLETED ? theme.palette.success.main : "inherit",
			textAlign: "center",
			fontSize: theme.typography.pxToRem(14),
			fontStyle: "normal",
			fontWeight: 700,
			lineHeight: theme.typography.pxToRem(22),
		},
	};
});

export const CalendarIcon = styled(CalendarIconSVG)(({ theme }) => {
	return {
		width: theme.typography.pxToRem(24),
		height: theme.typography.pxToRem(24),

		"& path": {
			fill: theme.palette.dirtyWhite["800"],
		},
	};
});

export const QuestionMarkIcon = styled(QuestionIconSVG)(({ theme }) => {
	return {
		width: theme.typography.pxToRem(24),
		height: theme.typography.pxToRem(24),

		"& path": {
			fill: theme.palette.dirtyWhite["800"],
		},
	};
});

export const TimeIcon = styled(TimeIconSVG)(({ theme }) => {
	return {
		width: theme.typography.pxToRem(24),
		height: theme.typography.pxToRem(24),

		"& path": {
			fill: theme.palette.dirtyWhite["800"],
		},
	};
});

export function LockedSection({ activity }: { activity: UserJourneyActivityResponse }) {
	const intl = useIntl();

	const theme = useTheme();

	const { lockedBy, lockedUntilDate } = activity;

	let lockedByString = "";

	if (lockedUntilDate && Date.now() < new Date(lockedUntilDate).getTime()) {
		const distance = formatDistance(new Date(lockedUntilDate), new Date(), { addSuffix: true });
		lockedByString = intl.formatMessage({ id: "USER-JOURNEY.activity.card.locked.open" }, { dateText: distance });
	}

	if (lockedBy.length > 0) {
		const total = lockedBy.length;
		const first = lockedBy[0];

		const prefix =
			first.type === JourneyActivityType.UPLOAD_FILE
				? intl.formatMessage({ id: "USER-JOURNEY.activity.card.locked.upload" })
				: intl.formatMessage({ id: "USER-JOURNEY.activity.card.locked.finish" });

		if (lockedByString) {
			lockedByString += ", ";
		}

		if (total === 1) {
			lockedByString += `${prefix} ${first.name}`;
		} else {
			lockedByString += `${prefix} ${first.name} ${intl.formatMessage({ id: "GLOBAL.and" })} ${
				total - 1
			} ${intl.formatMessage({ id: "GLOBAL.others" })}`;
		}
		lockedByString += " " + intl.formatMessage({ id: "USER-JOURNEY.activity.card.locked.to-open" });
	}

	return (
		<Stack
			alignItems="center"
			justifyContent="center"
			gap={{ sm: 1 }}
			p={{ xs: 1, sm: 3 }}
			border="2px dashed"
			borderColor="dirtyWhite.400"
			borderRadius={theme.spacing(2)}
			sx={{
				textAlign: "center",
				...theme.typography.sm,

				"& svg": {
					width: theme.spacing(4),
					height: theme.spacing(4),
				},
			}}
		>
			<InfoOutlinedIcon width={theme.spacing(4)} height={theme.spacing(4)} />
			{lockedByString}
		</Stack>
	);
}
